import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import Combobox from "../components/Combobox.jsx";
import {
  ENTRY_TYPES, NEW_ENTRY_TYPES, PAYMENT_SCOPES, CURRENCIES, PAYMENT_MODES, PARTY_TYPES,
  FACTORY_PAYMENT_TYPES, CUSTOMER_RECEIPT_TYPES, PAYMENT_TYPES,
  directionFromEntryType,
} from "../lib/constants";
import { toast } from "sonner";
import { useAuth } from "../lib/auth.jsx";
import { ArrowLeft, Save, PlusCircle, Trash2, Sparkles, Paperclip } from "lucide-react";
import ScanPanel from "../components/ScanPanel.jsx";
import SimilarWarning from "../components/SimilarWarning.jsx";

const todayISO = () => new Date().toISOString().slice(0, 10);
const caps = (v) => (v || "").toUpperCase();

const emptyForm = () => ({
  transaction_date: todayISO(),
  entry_type: "",
  pi_number: "",
  company: "",
  payment_mode: "",
  account: "",
  to_account: "",
  to_company: "",
  currency: "",
  to_currency: "",
  exchange_rate: "",
  amount: "",
  party_type: "",
  party_name: "",
  is_factory_payment: false,
  factory_payment_type: "",
  is_customer_receipt: false,
  customer_receipt_type: "",
  expense_category: "",
  payment_scope: "",
  purpose: "",
  notes: "",
  reference: "",
  status: "New",
  is_draft: false,
  attachments: [],
  ai_extraction: null,
});

export default function EntryForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEdit = Boolean(id) && id !== "new";
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingEntry, setLoadingEntry] = useState(isEdit);
  const [companies, setCompanies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [parties, setParties] = useState([]);
  const [pis, setPis] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadMasters = async () => {
    const [c, a, cat, p, pi] = await Promise.all([
      api.get("/masters/company"),
      api.get("/masters/account"),
      api.get("/masters/category"),
      api.get("/masters/party"),
      api.get("/masters/pi"),
    ]);
    setCompanies(c.data);
    setAccounts(a.data);
    setCategories(cat.data);
    setParties(p.data);
    setPis(pi.data);
  };
  useEffect(() => { loadMasters(); }, []);

  // Load existing entry when editing
  useEffect(() => {
    if (!isEdit) return;
    if (id === "new") { setLoadingEntry(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/transactions/${id}`);
        const base = emptyForm();
        // API returns null for unset strings; inputs need "" to avoid React null-value warnings
        const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v === null && typeof base[k] === "string" ? "" : v]));
        setForm({
          ...base,
          ...clean,
          amount: data.amount ?? "",
          exchange_rate: data.exchange_rate ?? "",
          transaction_date: (data.transaction_date || "").slice(0, 10),
        });
      } catch (e) {
        toast.error(formatApiError(e));
        nav("/entries");
      } finally {
        setLoadingEntry(false);
      }
    })();
  }, [id, isEdit, nav]);

  const addMaster = async (kind, name, extra = {}) => {
    try {
      const { data } = await api.post("/masters", { kind, name, ...extra });
      await loadMasters();
      return { value: data.name, label: data.name };
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  // Admin-only master rename (uses PUT /api/masters/:id shipped in Phase 2)
  const renameMaster = async (option) => {
    if (!isAdmin) return;
    const item = option?.meta;
    if (!item?.id) return;
    const newName = window.prompt(`Rename "${item.name}" to:`, item.name);
    if (!newName || newName.trim() === "" || newName.trim() === item.name) return;
    try {
      await api.put(`/masters/${item.id}`, { name: newName.trim() });
      toast.success("Renamed");
      await loadMasters();
      // If the form was using the old name, update it to the new one
      if (form.party_name === item.name) set("party_name", newName.trim());
      if (form.expense_category === item.name) set("expense_category", newName.trim());
      if (form.pi_number === item.name) set("pi_number", newName.trim());
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const direction = form.entry_type ? directionFromEntryType(form.entry_type) : "";
  const isTransfer = form.entry_type === "Transfer Between Own Accounts";
  const isExpense = form.entry_type === "Expense";
  const isMoneyPaid = form.entry_type === "Money Paid";
  const isMoneyReceived = form.entry_type === "Money Received";
  const isRefund = form.entry_type === "Refund Received" || form.entry_type === "Refund Paid";
  const isMoneyIn = direction === "Money In";
  const isMoneyOut = direction === "Money Out";

  // Scope toggles — client-side only, drive conditional rendering + auto-set flags
  const [paymentScope, setPaymentScope] = useState("");   // "PI/Order" | "Purchase/Stock" | "General/Office"
  const [receiptScope, setReceiptScope] = useState("");   // "PI/Customer" | "General/Other"

  // Effective scope: Expense is always General/Office; Money Paid follows toggle
  const effectivePaymentScope = isExpense ? "General/Office" : paymentScope;
  const isPIPayment = isMoneyPaid && effectivePaymentScope === "PI/Order";
  const isPurchasePayment = isMoneyPaid && effectivePaymentScope === "Purchase/Stock";
  const isGeneralPayment = (isMoneyPaid && effectivePaymentScope === "General/Office") || isExpense;
  const isPIReceipt = isMoneyReceived && receiptScope === "PI/Customer";
  const isGeneralReceipt = isMoneyReceived && receiptScope === "General/Other";

  // When editing, derive scope from existing data (stored payment_scope wins; legacy rows fall back to heuristics).
  // NOTE: expense_category derivation lives OUTSIDE the !paymentScope guard so it
  // is not lost when React strict-mode double-mounts the load effect (which re-runs
  // setForm and wipes intermediate derived state).
  useEffect(() => {
    if (!isEdit) return;
    if (form.entry_type === "Money Paid") {
      if (!paymentScope) {
        if (form.payment_scope) setPaymentScope(form.payment_scope);
        else setPaymentScope(form.is_factory_payment || form.pi_number || form.party_type === "Factory" ? "PI/Order" : "General/Office");
      }
      if (form.is_factory_payment && !form.expense_category) {
        setForm((f) => ({ ...f, expense_category: "Factory Payment", party_type: f.party_type || "Factory" }));
      }
    }
    if (form.entry_type === "Money Received") {
      if (!receiptScope) {
        setReceiptScope(form.is_customer_receipt || form.pi_number || form.party_type === "Customer" ? "PI/Customer" : "General/Other");
      }
      if (form.is_customer_receipt && !form.party_type) {
        setForm((f) => ({ ...f, party_type: "Customer" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, form.entry_type, form.is_factory_payment, form.is_customer_receipt, form.expense_category, form.payment_scope, id]);

  // Switching Payment Related To clears classification-specific fields so nothing leaks between scopes
  const handlePaymentScopeChange = (v) => {
    setPaymentScope(v);
    setErrors((er) => { const n = { ...er }; delete n.payment_scope; return n; });
    setForm((f) => ({
      ...f,
      is_factory_payment: false,
      factory_payment_type: "",
      expense_category: "",
      party_type: v === "Purchase/Stock" ? "Supplier" : "",
      party_name: "",
    }));
  };

  // Category lists: purchase categories (subtype="purchase") are only for Purchase / Stock
  const purchaseCategories = useMemo(() => categories.filter((c) => c.subtype === "purchase"), [categories]);
  const generalCategories = useMemo(
    () => categories.filter((c) => c.subtype !== "purchase" && c.name !== "Factory Payment"),
    [categories],
  );

  // When entry_type changes, reset scope-derived fields cleanly
  const handleEntryTypeChange = (v) => {
    set("entry_type", v);
    setPaymentScope("");
    setReceiptScope("");
    // Clear scope-dependent fields so state doesn't leak across entry types
    setForm((f) => ({
      ...f,
      entry_type: v,
      is_factory_payment: false,
      factory_payment_type: "",
      is_customer_receipt: false,
      customer_receipt_type: "",
      expense_category: "",
      party_type: "",
      party_name: "",
    }));
  };

  // Filter accounts based on payment_mode
  const filteredAccounts = useMemo(() => {
    if (!form.payment_mode) return accounts;
    const modeMap = {
      "Cash": "cash",
      "Bank": "bank",
      "Digital Wallet": "wallet",
      "Credit Card": "card",
      "Other": null,
    };
    const target = modeMap[form.payment_mode];
    if (!target) return accounts;
    return accounts.filter((a) => a.subtype === target);
  }, [accounts, form.payment_mode]);

  const filteredParties = useMemo(() => {
    if (!form.party_type) return parties;
    return parties.filter((p) => p.subtype === form.party_type);
  }, [parties, form.party_type]);

  const [errors, setErrors] = useState({});
  const [scanOpen, setScanOpen] = useState(false);

  // Possible-duplicate warning (never blocks). Debounced lookup keyed on the matching fields.
  const [similar, setSimilar] = useState(null);
  const [dismissedSig, setDismissedSig] = useState("");
  const similarSig = [form.amount, form.currency, form.party_name, form.company, form.account, form.pi_number, form.transaction_date, form.reference]
    .map((v) => String(v || "").trim().toLowerCase()).join("|");
  useEffect(() => {
    setDismissedSig("");
    if (!form.entry_type || !form.currency || !(Number(form.amount) > 0)) { setSimilar(null); return; }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post("/transactions/similar", {
          amount: Number(form.amount), currency: form.currency, party_name: form.party_name || null,
          company: form.company || null, account: form.account || null, pi_number: form.pi_number || null,
          transaction_date: form.transaction_date || null, reference: form.reference || null,
          exclude_id: isEdit ? id : null,
        });
        setSimilar(data);
      } catch { setSimilar(null); }
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [similarSig, form.entry_type]);
  const showSimilar = similar && similar.level !== "none" && dismissedSig !== similarSig;

  const applyScanPatch = ({ formPatch, attachments, aiExtraction }) => {
    setForm((f) => {
      const next = { ...f };
      Object.entries(formPatch || {}).forEach(([k, v]) => {
        if (v === undefined || v === "" || v === null) return;
        next[k] = v;
      });
      next.attachments = [...(f.attachments || []), ...(attachments || [])];
      next.ai_extraction = aiExtraction || f.ai_extraction || null;
      // If entry_type is missing, default to Money Received (most common scan target)
      if (!next.entry_type) next.entry_type = "Money Received";
      return next;
    });
    setScanOpen(false);
    toast.success("Fields applied — review and save");
  };

  const removeAttachment = (fid) => {
    setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((a) => a.file_id !== fid) }));
  };

  const validate = () => {
    const e = {};
    if (!form.entry_type) e.entry_type = "Please select an entry type";
    if (!form.transaction_date) e.transaction_date = "Date is required";
    if (!form.currency) e.currency = "Currency is required";
    if (!form.amount || Number(form.amount) <= 0) e.amount = "Amount must be greater than 0";
    if (isTransfer) {
      if (!form.account) e.account = "From account is required";
      if (!form.to_account) e.to_account = "To account is required";
      if (form.account && form.to_account && form.account === form.to_account) {
        e.to_account = "Must differ from source account";
      }
    } else if (form.entry_type) {
      if (!form.company) e.company = "Company is required";
      if (!form.payment_mode) e.payment_mode = "Choose Cash / Bank / Wallet";
      if (!form.account) e.account = "Payment account is required";
    }
    // Scope selection is required for Money Paid & Money Received
    if (isMoneyPaid && !paymentScope) e.payment_scope = "Please choose Payment Related To";
    if (isMoneyReceived && !receiptScope) e.receipt_scope = "Please choose Receipt Related To";
    // Category required for Expense and General/Office Money Paid
    if ((isExpense || isGeneralPayment) && !form.expense_category) {
      e.expense_category = isExpense ? "Expense category is required" : "Category is required";
    }
    // Purchase / Stock: purchase category + supplier required
    if (isPurchasePayment) {
      if (!form.expense_category) e.expense_category = "Purchase category is required";
      if (!form.party_name) e.party_name = "Supplier / Paid To is required";
    }
    // Payment Type required for PI/Order Money Paid
    if (isPIPayment && !form.expense_category) e.expense_category = "Please choose a Payment Type";
    // Factory required when Payment Type = Factory Payment
    if (isPIPayment && form.expense_category === "Factory Payment" && !form.party_name) {
      e.party_name = "Factory is required";
    }
    return e;
  };

  const focusField = (name) => {
    // Wait a tick for React to render error styling
    setTimeout(() => {
      const wrapper = document.querySelector(`[data-field="${name}"]`);
      if (!wrapper) return;
      wrapper.scrollIntoView({ behavior: "smooth", block: "center" });
      const target = wrapper.querySelector('input, textarea, button[type="button"]');
      if (target) {
        try { target.focus({ preventScroll: true }); } catch { target.focus(); }
      }
    }, 50);
  };

  const save = async (mode) => {
    const errs = validate();
    setErrors(errs);
    const keys = Object.keys(errs);
    if (keys.length > 0) {
      toast.error(errs[keys[0]]);
      focusField(keys[0]);
      return;
    }
    if (showSimilar && similar.level === "duplicate" && !isEdit) {
      if (!window.confirm("A possible duplicate transaction was found. Save this as a NEW transaction anyway?")) return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        exchange_rate: form.exchange_rate ? Number(form.exchange_rate) : null,
        payment_scope: isMoneyPaid ? paymentScope : null,
        is_draft: mode === "draft",
      };
      // Attach reviewer metadata if AI extraction was applied
      if (form.ai_extraction && !form.reviewed_at) {
        payload.reviewed_at = new Date().toISOString();
      }
      if (isEdit) {
        await api.put(`/transactions/${id}`, payload);
        toast.success("Entry updated");
        nav("/entries");
        return;
      }
      await api.post("/transactions", payload);
      toast.success(mode === "draft" ? "Saved as draft" : "Entry saved");
      if (mode === "another") {
        setForm({
          ...emptyForm(),
          transaction_date: form.transaction_date,
          entry_type: form.entry_type,
          company: form.company,
          payment_mode: form.payment_mode,
          account: form.account,
          currency: form.currency,
        });
        setErrors({});
      } else {
        nav("/entries");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Clear error for a field when its value changes
  useEffect(() => {
    setErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((k) => {
        if (form[k] || (k === "amount" && Number(form.amount) > 0)) {
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [form]);

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Entry deleted");
      nav("/entries");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-1 mb-4" data-testid="back-btn">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            {isEdit ? "Edit Money Entry" : "Add Money Entry"}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            {isEdit ? "Update this transaction" : "One form for every financial transaction"}
          </p>
        </div>
        {!isEdit && (
          <button
            type="button"
            onClick={() => setScanOpen(true)}
            data-testid="scan-open-btn"
            className="inline-flex items-center gap-2 h-12 px-5 rounded-xl bg-gradient-to-r from-[#0055FF] to-[#0044CC] text-white font-semibold shadow-sm hover:opacity-95 transition-opacity"
          >
            <Sparkles size={18} /> Scan payment proof
          </button>
        )}
      </div>

      {form.ai_extraction && (
        <div className="mb-6 rounded-xl border border-[#0055FF]/30 bg-blue-50/60 p-3 text-xs text-slate-700 flex items-start gap-2" data-testid="ai-applied-banner">
          <Sparkles size={14} className="text-[#0055FF] mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold text-slate-900">AI-extracted values applied.</span> Fields below are pre-filled — please review each one before saving.
            <span className="ml-2 text-slate-500 font-mono text-[10px]">{form.ai_extraction.provider}/{form.ai_extraction.model}</span>
          </div>
        </div>
      )}

      {loadingEntry ? (
        <div className="text-slate-400">Loading entry…</div>
      ) : (
      <div className="space-y-8 max-w-3xl">
        {/* SECTION 1: Basics */}
        <Section title="1 · Entry basics">
          <Grid>
            <Field label="Transaction Date" name="transaction_date" error={errors.transaction_date}>
              <input
                type="date"
                value={form.transaction_date}
                onChange={(e) => set("transaction_date", e.target.value)}
                data-testid="field-date"
                className="w-full h-[52px] px-4 border border-slate-300 rounded-xl bg-white text-base outline-none"
              />
            </Field>
            <Field label="Entry Type" required name="entry_type" error={errors.entry_type}>
              <Combobox
                value={form.entry_type}
                onChange={handleEntryTypeChange}
                options={(form.entry_type === "Expense" ? ENTRY_TYPES : NEW_ENTRY_TYPES).map((t) => ({ value: t, label: t }))}
                placeholder="Select entry type…"
                testid="field-entry-type"
              />
            </Field>
          </Grid>
          {direction && (
            <div className="mt-3 text-xs text-slate-500">
              Direction: <span className="font-semibold text-slate-800">{direction}</span>
            </div>
          )}
        </Section>

        {/* SECTION 2: Company & Account (or Transfer accounts) */}
        {form.entry_type && (
          <Section title={isTransfer ? "2 · Transfer accounts" : "2 · Company & account"}>
            {isTransfer ? (
              <>
                <Grid>
                  <Field label="From Company">
                    <Combobox
                      value={form.company}
                      onChange={(v) => set("company", v)}
                      options={companies.map((c) => ({ value: c.name, label: c.name }))}
                      onAddNew={(n) => addMaster("company", n)}
                      testid="field-from-company"
                      placeholder="From company…"
                    />
                  </Field>
                  <Field label="From Account" required name="account" error={errors.account}>
                    <Combobox
                      value={form.account}
                      onChange={(v) => set("account", v)}
                      options={accounts.map((a) => ({ value: a.name, label: `${a.name} (${a.currency})` }))}
                      testid="field-from-account"
                      placeholder="Account…"
                    />
                  </Field>
                </Grid>
                <Grid>
                  <Field label="To Company">
                    <Combobox
                      value={form.to_company}
                      onChange={(v) => set("to_company", v)}
                      options={companies.map((c) => ({ value: c.name, label: c.name }))}
                      onAddNew={(n) => addMaster("company", n)}
                      testid="field-to-company"
                      placeholder="To company…"
                    />
                  </Field>
                  <Field label="To Account" required name="to_account" error={errors.to_account}>
                    <Combobox
                      value={form.to_account}
                      onChange={(v) => set("to_account", v)}
                      options={accounts.map((a) => ({ value: a.name, label: `${a.name} (${a.currency})` }))}
                      testid="field-to-account"
                      placeholder="Account…"
                    />
                  </Field>
                </Grid>
              </>
            ) : (
              <>
                <Grid>
                  <Field label="Transaction Company" required name="company" error={errors.company}>
                    <Combobox
                      value={form.company}
                      onChange={(v) => set("company", v)}
                      options={companies.map((c) => ({ value: c.name, label: c.name }))}
                      onAddNew={(n) => addMaster("company", n)}
                      testid="field-company"
                      placeholder="By which company?"
                    />
                  </Field>
                  <Field label="Cash / Bank / Wallet" required name="payment_mode" error={errors.payment_mode}>
                    <Combobox
                      value={form.payment_mode}
                      onChange={(v) => { set("payment_mode", v); set("account", ""); }}
                      options={PAYMENT_MODES.map((m) => ({ value: m, label: m }))}
                      testid="field-payment-mode"
                      placeholder="Select mode…"
                    />
                  </Field>
                </Grid>
                <Field label="Payment Account" required name="account" error={errors.account}>
                  <Combobox
                    value={form.account}
                    onChange={(v) => set("account", v)}
                    options={filteredAccounts.map((a) => ({ value: a.name, label: `${a.name}${a.currency ? " (" + a.currency + ")" : ""}` }))}
                    onAddNew={(n) => addMaster("account", n, { subtype: form.payment_mode?.toLowerCase().includes("bank") ? "bank" : form.payment_mode?.toLowerCase().includes("cash") ? "cash" : "wallet" })}
                    testid="field-account"
                    placeholder={form.payment_mode ? "Select account…" : "Choose mode first"}
                    disabled={!form.payment_mode}
                  />
                </Field>
              </>
            )}
          </Section>
        )}

        {/* SECTION 3: Amount & Currency */}
        {form.entry_type && (
          <Section title="3 · Amount">
            <Grid>
              <Field label="Currency" required name="currency" error={errors.currency}>
                <Combobox
                  value={form.currency}
                  onChange={(v) => set("currency", v)}
                  options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                  testid="field-currency"
                  placeholder="Select currency…"
                />
              </Field>
              <Field label="Amount" required name="amount" error={errors.amount}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => set("amount", e.target.value)}
                  data-testid="field-amount"
                  className="w-full h-[52px] px-4 border border-slate-300 rounded-xl bg-white text-lg tabular text-right outline-none"
                  placeholder="0.00"
                />
              </Field>
            </Grid>
            {isTransfer && (
              <Grid>
                <Field label="To Currency (if different)">
                  <Combobox
                    value={form.to_currency}
                    onChange={(v) => set("to_currency", v)}
                    options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                    testid="field-to-currency"
                    placeholder="Same as source"
                  />
                </Field>
                <Field label="Exchange Rate">
                  <input
                    type="number"
                    step="0.0001"
                    value={form.exchange_rate}
                    onChange={(e) => set("exchange_rate", e.target.value)}
                    data-testid="field-exchange-rate"
                    className="w-full h-[52px] px-4 border border-slate-300 rounded-xl bg-white text-base tabular text-right outline-none"
                    placeholder="e.g. 7.25"
                  />
                </Field>
              </Grid>
            )}
          </Section>
        )}

        {/* Scope toggle for Money Paid / Money Received — the key UX simplification */}
        {isMoneyPaid && (
          <Section title="4 · Payment Related To">
            <ScopePicker
              value={paymentScope}
              onChange={handlePaymentScopeChange}
              options={PAYMENT_SCOPES}
              testid="scope-payment"
              error={errors.payment_scope}
            />
          </Section>
        )}
        {isMoneyReceived && (
          <Section title="4 · Receipt Related To">
            <ScopePicker
              value={receiptScope}
              onChange={(v) => { setReceiptScope(v); setErrors((er) => { const n={...er}; delete n.receipt_scope; return n; }); }}
              options={[
                { value: "PI/Customer", label: "PI / Customer", hint: "Customer payment against a PI" },
                { value: "General/Other", label: "General / Other Income", hint: "Interest, rebate, other income" },
              ]}
              testid="scope-receipt"
              error={errors.receipt_scope}
            />
          </Section>
        )}

        {/* SECTION 5A: PI Payment — Payment Type & Factory/Party */}
        {isPIPayment && (
          <Section title="5 · Payment details">
            <Field label="Payment Type" required name="expense_category" error={errors.expense_category}>
              <Combobox
                value={form.expense_category}
                onChange={(v) => {
                  setForm((f) => ({
                    ...f,
                    expense_category: v,
                    is_factory_payment: v === "Factory Payment",
                    // Auto-set party_type when Factory Payment
                    party_type: v === "Factory Payment" ? "Factory" : (f.party_type || ""),
                    party_name: v === "Factory Payment" ? "" : f.party_name,
                    factory_payment_type: v === "Factory Payment" ? f.factory_payment_type : "",
                  }));
                }}
                options={PAYMENT_TYPES.map((t) => ({ value: t, label: t }))}
                testid="field-payment-type"
                placeholder="Select payment type…"
              />
            </Field>

            {form.expense_category === "Factory Payment" && (
              <>
                <Grid>
                  <Field label="Factory" required name="party_name" error={errors.party_name}>
                    <Combobox
                      value={form.party_name}
                      onChange={(v) => set("party_name", v)}
                      options={parties.filter((p) => p.subtype === "Factory").map((p) => ({ value: p.name, label: p.name, meta: p }))}
                      onAddNew={(n) => addMaster("party", n, { subtype: "Factory" })}
                      onEditOption={isAdmin ? renameMaster : undefined}
                      testid="field-factory-name"
                      placeholder="Select or add factory…"
                    />
                  </Field>
                  <Field label="Factory Payment Type">
                    <Combobox
                      value={form.factory_payment_type}
                      onChange={(v) => set("factory_payment_type", v)}
                      options={FACTORY_PAYMENT_TYPES.map((t) => ({ value: t, label: t }))}
                      testid="field-factory-type"
                      placeholder="Advance / Second / Balance…"
                    />
                  </Field>
                </Grid>
              </>
            )}

            {form.expense_category && form.expense_category !== "Factory Payment" && (
              <Grid>
                <Field label="Party Type (optional)">
                  <Combobox
                    value={form.party_type}
                    onChange={(v) => { set("party_type", v); set("party_name", ""); }}
                    options={PARTY_TYPES.map((p) => ({ value: p, label: p }))}
                    testid="field-party-type"
                    placeholder="Supplier / Agent / …"
                  />
                </Field>
                <Field label="Party / Paid To (optional)">
                  <Combobox
                    value={form.party_name}
                    onChange={(v) => set("party_name", v)}
                    options={filteredParties.map((p) => ({ value: p.name, label: p.name, meta: p }))}
                    onAddNew={(n) => addMaster("party", n, { subtype: form.party_type || "Other" })}
                    onEditOption={isAdmin ? renameMaster : undefined}
                    testid="field-party-name"
                    placeholder={form.party_type ? "Select or add…" : "Choose party type first"}
                    disabled={!form.party_type}
                  />
                </Field>
              </Grid>
            )}
          </Section>
        )}

        {/* SECTION 5B: General/Office Payment (also used by Expense) */}
        {isGeneralPayment && (
          <Section title="5 · Expense details">
            <Field label={isExpense ? "Expense Category" : "Expense Category"} required name="expense_category" error={errors.expense_category}>
              <Combobox
                value={form.expense_category}
                onChange={(v) => set("expense_category", v)}
                options={generalCategories.map((c) => ({ value: c.name, label: c.name, meta: c }))}
                onAddNew={(n) => addMaster("category", n)}
                onEditOption={isAdmin ? renameMaster : undefined}
                testid="field-expense-category"
                placeholder="Select category…"
              />
            </Field>
            <Field label="Paid To (optional)">
              <input
                value={form.party_name}
                onChange={(e) => set("party_name", caps(e.target.value))}
                data-testid="field-paid-to"
                className="w-full h-[52px] px-4 border border-slate-300 rounded-xl bg-white text-base outline-none uppercase placeholder:normal-case"
                placeholder="e.g. Taxi driver, Utility, Vendor…"
              />
            </Field>
          </Section>
        )}

        {/* SECTION 5F: Purchase / Stock — financial classification only (no inventory tracking) */}
        {isPurchasePayment && (
          <Section title="5 · Purchase details">
            <Grid>
              <Field label="Purchase Category" required name="expense_category" error={errors.expense_category}>
                <Combobox
                  value={form.expense_category}
                  onChange={(v) => set("expense_category", v)}
                  options={purchaseCategories.map((c) => ({ value: c.name, label: c.name, meta: c }))}
                  onAddNew={(n) => addMaster("category", n, { subtype: "purchase" })}
                  onEditOption={isAdmin ? renameMaster : undefined}
                  testid="field-purchase-category"
                  placeholder="Bobbins / Needles / Spare Parts…"
                />
              </Field>
              <Field label="Supplier / Paid To" required name="party_name" error={errors.party_name}>
                <Combobox
                  value={form.party_name}
                  onChange={(v) => setForm((f) => ({ ...f, party_name: v, party_type: "Supplier" }))}
                  options={parties.filter((p) => p.subtype === "Supplier").map((p) => ({ value: p.name, label: p.name, meta: p }))}
                  onAddNew={(n) => addMaster("party", n, { subtype: "Supplier" })}
                  onEditOption={isAdmin ? renameMaster : undefined}
                  testid="field-supplier-name"
                  placeholder="Select or add supplier…"
                />
              </Field>
            </Grid>
          </Section>
        )}

        {/* SECTION 5C: PI Customer Receipt */}
        {isPIReceipt && (
          <Section title="5 · Customer & PI">
            <Grid>
              <Field label="Customer" name="party_name" error={errors.party_name}>
                <Combobox
                  value={form.party_name}
                  onChange={(v) => {
                    setForm((f) => ({ ...f, party_name: v, party_type: "Customer", is_customer_receipt: true }));
                  }}
                  options={parties.filter((p) => p.subtype === "Customer").map((p) => ({ value: p.name, label: p.name, meta: p }))}
                  onAddNew={(n) => addMaster("party", n, { subtype: "Customer" })}
                  onEditOption={isAdmin ? renameMaster : undefined}
                  testid="field-customer-name"
                  placeholder="Select or add customer…"
                />
              </Field>
              <Field label="Receipt Type">
                <Combobox
                  value={form.customer_receipt_type}
                  onChange={(v) => set("customer_receipt_type", v)}
                  options={CUSTOMER_RECEIPT_TYPES.map((t) => ({ value: t, label: t }))}
                  testid="field-customer-type"
                  placeholder="Advance / Second / Balance…"
                />
              </Field>
            </Grid>
          </Section>
        )}

        {/* SECTION 5D: General/Other Income */}
        {isGeneralReceipt && (
          <Section title="5 · Income details">
            <Grid>
              <Field label="Income Category (optional)">
                <Combobox
                  value={form.expense_category}
                  onChange={(v) => set("expense_category", v)}
                  options={generalCategories.map((c) => ({ value: c.name, label: c.name, meta: c }))}
                  onAddNew={(n) => addMaster("category", n)}
                  onEditOption={isAdmin ? renameMaster : undefined}
                  testid="field-income-category"
                  placeholder="Select or add category…"
                />
              </Field>
              <Field label="Received From (optional)">
                <input
                  value={form.party_name}
                  onChange={(e) => set("party_name", caps(e.target.value))}
                  data-testid="field-received-from"
                  className="w-full h-[52px] px-4 border border-slate-300 rounded-xl bg-white text-base outline-none uppercase placeholder:normal-case"
                  placeholder="e.g. Interest, Rebate, Vendor…"
                />
              </Field>
            </Grid>
          </Section>
        )}

        {/* SECTION 5E: Refund — party optional, no auto-classify */}
        {isRefund && (
          <Section title="5 · Party (optional)">
            <Grid>
              <Field label="Party Type">
                <Combobox
                  value={form.party_type}
                  onChange={(v) => { set("party_type", v); set("party_name", ""); }}
                  options={PARTY_TYPES.map((p) => ({ value: p, label: p }))}
                  testid="field-party-type"
                  placeholder="Customer / Factory / Supplier / …"
                />
              </Field>
              <Field label="Party Name">
                <Combobox
                  value={form.party_name}
                  onChange={(v) => set("party_name", v)}
                  options={filteredParties.map((p) => ({ value: p.name, label: p.name, meta: p }))}
                  onAddNew={(n) => addMaster("party", n, { subtype: form.party_type || "Other" })}
                  onEditOption={isAdmin ? renameMaster : undefined}
                  testid="field-party-name"
                  placeholder={form.party_type ? "Select or add…" : "Choose type first"}
                  disabled={!form.party_type}
                />
              </Field>
            </Grid>
          </Section>
        )}

        {/* PI & notes — PI hidden for General scopes; whole section hidden until scope is picked for Money Paid/Received */}
        {form.entry_type && (!isMoneyPaid || paymentScope) && (!isMoneyReceived || receiptScope) && (
          <Section title="6 · PI & details">
            <Grid>
              {!isGeneralPayment && !isGeneralReceipt && !isTransfer && (
                <Field label={isPIPayment || isPIReceipt ? "PI Number" : "PI Number (optional)"}>
                  <Combobox
                    value={form.pi_number}
                    onChange={(v) => set("pi_number", v)}
                    options={pis.map((p) => ({ value: p.name, label: p.name }))}
                    onAddNew={(n) => addMaster("pi", n)}
                    testid="field-pi"
                    placeholder="Search or add PI…"
                  />
                </Field>
              )}
              <Field label="Reference Number">
                <input
                  value={form.reference}
                  onChange={(e) => set("reference", caps(e.target.value))}
                  data-testid="field-reference"
                  className="w-full h-[52px] px-4 border border-slate-300 rounded-xl bg-white text-base outline-none uppercase placeholder:normal-case"
                  placeholder="Bank ref / SWIFT / Cheque no…"
                />
              </Field>
            </Grid>
            {!isGeneralPayment && !isGeneralReceipt && !isTransfer && !isPurchasePayment && (
              <Field label={isPIReceipt ? "Purpose" : "Payment Purpose"}>
                <input
                  value={form.purpose}
                  onChange={(e) => set("purpose", e.target.value)}
                  data-testid="field-purpose"
                  className="w-full h-[52px] px-4 border border-slate-300 rounded-xl bg-white text-base outline-none"
                  placeholder="e.g. Factory advance for PI 41"
                />
              </Field>
            )}
            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                data-testid="field-notes"
                rows={3}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white text-base outline-none"
                placeholder="Any additional details…"
              />
            </Field>
          </Section>
        )}

        {/* Attachments */}
        {form.entry_type && (form.attachments?.length > 0) && (
          <Section title="7 · Attachments">
            <div className="space-y-2" data-testid="entry-attachments">
              {form.attachments.map((a) => (
                <div key={a.file_id} className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2">
                  <Paperclip size={16} className="text-slate-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{a.filename}</div>
                    <div className="text-xs text-slate-500">{a.size ? (a.size/1024).toFixed(1) + " KB" : ""} · {a.mime_type}</div>
                  </div>
                  <button onClick={() => removeAttachment(a.file_id)} className="text-slate-400 hover:text-red-600" data-testid={`entry-remove-attachment-${a.file_id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Possible duplicate warning — informational only, never blocks */}
        {form.entry_type && showSimilar && (
          <SimilarWarning result={similar} isMoneyOut={isMoneyOut} onContinue={() => setDismissedSig(similarSig)} />
        )}

        {/* Actions */}
        {form.entry_type && (
          <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 -mx-4 sm:mx-0 px-4 sm:px-0 pt-4 pb-4 sm:pb-0 sm:relative sm:border-0 sm:backdrop-blur-0 sm:bg-transparent">
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              {isEdit && (
                <button
                  type="button"
                  onClick={remove}
                  disabled={deleting}
                  data-testid="delete-btn"
                  className="inline-flex items-center gap-2 justify-center px-6 rounded-xl border-2 border-red-200 text-red-600 hover:bg-red-50 font-semibold transition-colors disabled:opacity-60 sm:mr-auto"
                  style={{ height: "52px" }}
                >
                  <Trash2 size={18} /> {deleting ? "Deleting…" : "Delete"}
                </button>
              )}
              <button
                type="button"
                onClick={() => nav(-1)}
                data-testid="cancel-btn"
                className="h-13 px-6 rounded-xl border-2 border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold transition-colors"
                style={{ height: "52px" }}
              >
                Cancel
              </button>
              {!isEdit && (
                <>
                  <button
                    type="button"
                    onClick={() => save("draft")}
                    disabled={saving}
                    data-testid="save-draft-btn"
                    className="px-6 rounded-xl border-2 border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold transition-colors disabled:opacity-60"
                    style={{ height: "52px" }}
                  >
                    Save Draft
                  </button>
                  <button
                    type="button"
                    onClick={() => save("another")}
                    disabled={saving}
                    data-testid="save-another-btn"
                    className="inline-flex items-center gap-2 justify-center px-6 rounded-xl border-2 border-[#0055FF] text-[#0055FF] hover:bg-blue-50 font-semibold transition-colors disabled:opacity-60"
                    style={{ height: "52px" }}
                  >
                    <PlusCircle size={18} /> Save & Add Another
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => save("save")}
                disabled={saving}
                data-testid="save-btn"
                className="inline-flex items-center gap-2 justify-center px-6 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold transition-colors disabled:opacity-60"
                style={{ height: "52px" }}
              >
                <Save size={18} /> {saving ? "Saving…" : (isEdit ? "Update Entry" : "Save Entry")}
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {scanOpen && (
        <ScanPanel
          onClose={() => setScanOpen(false)}
          onApply={applyScanPatch}
          masters={{ companies, accounts, categories, parties, pis }}
          addMaster={addMaster}
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="section-label mb-3">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}
function Field({ label, required, name, error, children }) {
  return (
    <div data-field={name} className={error ? "field-has-error" : ""}>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1.5 text-xs font-medium text-red-600" data-testid={name ? `field-${name}-error` : undefined}>
          {error}
        </p>
      )}
    </div>
  );
}
function ToggleRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
      <span className="text-sm font-medium text-slate-800">{label}</span>
      <div className="flex gap-2">
        {[["Yes", true], ["No", false]].map(([l, v]) => (
          <button
            key={l}
            type="button"
            onClick={() => onChange(v)}
            data-testid={`${testid}-${l.toLowerCase()}`}
            className={`px-4 h-9 rounded-lg text-sm font-semibold transition-colors ${
              value === v ? "bg-[#0055FF] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function ScopePicker({ value, onChange, options, testid, error }) {
  const cols = options.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
  return (
    <div data-field={testid} className={error ? "field-has-error" : ""}>
      <div className={`grid grid-cols-1 ${cols} gap-3`}>
        {options.map((o) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              data-testid={`${testid}-${o.value.replace(/[^a-zA-Z0-9]/g, "")}`}
              className={`min-h-16 py-3 rounded-2xl border-2 text-left px-5 transition-colors ${
                active
                  ? "border-[#0055FF] bg-blue-50/60"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div className={`text-base font-semibold ${active ? "text-[#0055FF]" : "text-slate-900"}`}>
                {o.label}
              </div>
              {o.hint && <div className="text-xs text-slate-500 mt-0.5">{o.hint}</div>}
            </button>
          );
        })}
      </div>
      {error && <p className="mt-1.5 text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
}
